/**
 * Correct one catalogue field — OND-41.
 *
 * A SELECT, A SENTENCE, A FIELD AND ONE BUTTON. The sentence says what the
 * record says now for the chosen field, so the reader is correcting a value
 * they can see rather than typing blind; the field is what it should say; the
 * button adds the correction. There is no edit-in-place anywhere on this
 * screen and this panel is why: a correction is a row that says what was wrong,
 * and the identity block above marks the value as corrected from it.
 *
 * Renders nothing for anybody the model hands null: the owner of the holding
 * collection and the attributed estate are the two parties
 * `ondo.correct_work()` admits, and a panel drawn for a viewer would be a
 * control the database will refuse. The estate's RUNG is the database's to
 * check, so an estate viewer sees the panel and reads the refusal — which is
 * printed, because it is the database's own sentence.
 */
import { useId } from "react";

import { WORK_CORRECTION_COPY } from "./work.constants.ts";
import type { CorrectionPanel } from "./work.model.ts";
import type { WorkCorrectionField } from "@ondo/shared";

import "./work.css";

type Props = {
  panel: CorrectionPanel | null;
  busy: boolean;
  failed: string | null;
  onChooseField: (field: WorkCorrectionField) => void;
  onChangeValue: (value: string) => void;
  onSubmit: () => void;
};

export function WorkCorrectionPanel({
  panel,
  busy,
  failed,
  onChooseField,
  onChangeValue,
  onSubmit,
}: Props) {
  const fieldId = useId();
  const valueId = useId();
  if (panel === null) return null;

  return (
    <section className="work__panel work-correction" aria-labelledby={`${fieldId}-heading`}>
      <h3 id={`${fieldId}-heading`} className="work__panel-head">
        {WORK_CORRECTION_COPY.heading}
      </h3>
      <p className="work-correction__lead">{WORK_CORRECTION_COPY.lead}</p>

      <form
        className="work-correction__form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <label htmlFor={fieldId}>{WORK_CORRECTION_COPY.fieldLabel}</label>
        <select
          id={fieldId}
          value={panel.field}
          disabled={busy}
          onChange={(event) => onChooseField(event.target.value as WorkCorrectionField)}
        >
          {panel.fields.map((field) => (
            <option key={field.field} value={field.field}>
              {field.label}
            </option>
          ))}
        </select>

        <p className="work-correction__current">
          <span className="work-correction__current-label">{WORK_CORRECTION_COPY.currently}</span>{" "}
          <q>{panel.current}</q>
        </p>

        <label htmlFor={valueId}>{WORK_CORRECTION_COPY.valueLabel}</label>
        <input
          id={valueId}
          type="text"
          value={panel.value}
          disabled={busy}
          onChange={(event) => onChangeValue(event.target.value)}
        />

        <button type="submit" className="work-correction__action" disabled={busy || panel.value.trim() === ""}>
          {WORK_CORRECTION_COPY.action}
        </button>
        <p className="work-correction__note">{WORK_CORRECTION_COPY.isFinal}</p>
        {failed && (
          <p role="alert" className="work-correction__state work-correction__state--failed">
            {failed}
          </p>
        )}
      </form>
    </section>
  );
}
